import { useEffect, RefObject } from 'react';

/**
 * Custom hook to handle click outside of an element
 * 
 * @param ref - Reference to the element
 * @param callback - Callback function to execute when clicking outside
 * @param isActive - Whether the hook is active (default: true)
 * 
 * @example
 * ```tsx
 * import { useRef, useState, useCallback } from 'react';
 * import { useClickOutside } from './useClickOutside';
 * 
 * const MyComponent = () => {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const popoverRef = useRef<HTMLDivElement>(null);
 * 
 *   // Close popover when clicking outside
 *   useClickOutside(
 *     popoverRef,
 *     useCallback(() => setIsOpen(false), []),
 *     isOpen // Only active when popover is open
 *   );
 * 
 *   return (
 *     <div>
 *       <button onClick={() => setIsOpen(!isOpen)}>Toggle</button>
 *       {isOpen && (
 *         <div ref={popoverRef} className="popover">
 *           Popover content
 *         </div>
 *       )}
 *     </div>
 *   );
 * };
 * ```
 * 
 * @remarks
 * - The callback should be wrapped in useCallback to prevent unnecessary re-renders
 * - The isActive parameter allows you to conditionally enable/disable the hook
 * - The hook uses 'mousedown' event for better performance
 */
export const useClickOutside = (
  ref: RefObject<HTMLElement>,
  callback: () => void,
  isActive: boolean = true
) => {
  useEffect(() => {
    if (!isActive) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      if (ref.current && !ref.current.contains(target)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callback, isActive]);
};

